import { useEffect, useState } from "react";
import { Upload, Save, Download } from "lucide-react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import AppLayout from "@/components/AppLayout";
import PageHeader from "@/components/PageHeader";
import DataTable, { Column } from "@/components/DataTable";
import { LoadingState, EmptyState } from "@/components/LoadingState";
import { buildingApi, meterApi, readingApi, unitApi } from "@/api/endpoints";
import { useOrgId } from "@/hooks/useOrgId";
import type { BuildingSummary, CsvImportResult, Meter, Unit } from "@/types";

type Tab = "manual" | "csv";

interface ManualForm {
  building_id: string;
  unit_id: string;
  meter_id: string;
  reading_date: string;
  raw_value: number;
  notes?: string;
}

export default function ReadingsPage() {
  const orgId = useOrgId();
  const [tab, setTab] = useState<Tab>("manual");

  const [buildings, setBuildings] = useState<BuildingSummary[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [meters, setMeters] = useState<Meter[]>([]);
  const [csvResult, setCsvResult] = useState<CsvImportResult | null>(null);
  const [uploading, setUploading] = useState(false);

  // datetime-local input wants `YYYY-MM-DDTHH:MM` in local time.
  const _nowLocal = () => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const form = useForm<ManualForm>({
    defaultValues: { reading_date: _nowLocal() },
  });
  const buildingId = form.watch("building_id");
  const unitId = form.watch("unit_id");

  useEffect(() => {
    if (orgId) buildingApi.list(orgId).then(setBuildings);
  }, [orgId]);

  useEffect(() => {
    setUnits([]);
    setMeters([]);
    form.setValue("unit_id", "");
    form.setValue("meter_id", "");
    if (buildingId) unitApi.list(buildingId).then(setUnits);
  }, [buildingId]);

  useEffect(() => {
    setMeters([]);
    form.setValue("meter_id", "");
    if (unitId) meterApi.list(unitId).then(setMeters);
  }, [unitId]);

  const submitManual = form.handleSubmit(async (values) => {
    try {
      // datetime-local gives us `YYYY-MM-DDTHH:MM` without a TZ. Treat as local
      // time and convert to ISO 8601 (UTC) before sending.
      const iso = new Date(values.reading_date).toISOString();
      await readingApi.submit({
        meter_id: values.meter_id,
        reading_date: iso,
        raw_value: Number(values.raw_value),
        notes: values.notes,
      });
      toast.success("Reading saved");
      form.reset({
        reading_date: _nowLocal(),
        building_id: values.building_id,
        unit_id: values.unit_id,
        meter_id: values.meter_id,
      });
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  });

  const handleFile = async (file: File) => {
    setUploading(true);
    setCsvResult(null);
    try {
      const result = await readingApi.uploadCsv(file);
      setCsvResult(result);
      toast.success(`Imported ${result.imported} reading${result.imported === 1 ? "" : "s"}`);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <AppLayout>
      <PageHeader title="Readings" subtitle="Enter manual readings or upload a CSV batch" />
      <div className="card overflow-hidden mb-6">
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setTab("manual")}
            className={`px-5 py-3 text-sm font-medium ${tab === "manual" ? "text-brand-700 border-b-2 border-brand-700" : "text-slate-600 hover:text-slate-900"}`}
            type="button"
          >
            Manual entry
          </button>
          <button
            onClick={() => setTab("csv")}
            className={`px-5 py-3 text-sm font-medium ${tab === "csv" ? "text-brand-700 border-b-2 border-brand-700" : "text-slate-600 hover:text-slate-900"}`}
            type="button"
          >
            CSV upload
          </button>
        </div>
        <div className="p-6">
          {tab === "manual" && (
            <form onSubmit={submitManual} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Building</label>
                <select className="input" {...form.register("building_id", { required: true })}>
                  <option value="">Select…</option>
                  {buildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Unit</label>
                <select className="input" {...form.register("unit_id", { required: true })} disabled={!units.length}>
                  <option value="">Select…</option>
                  {units.map((u) => <option key={u.id} value={u.id}>{u.unit_number}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Meter</label>
                <select className="input" {...form.register("meter_id", { required: true })} disabled={!meters.length}>
                  <option value="">Select…</option>
                  {meters.map((m) => (
                    <option key={m.id} value={m.id}>{m.meter_number} · {m.utility_type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Reading date &amp; time</label>
                <input
                  type="datetime-local"
                  className="input"
                  {...form.register("reading_date", { required: true })}
                />
              </div>
              <div>
                <label className="label">Reading value</label>
                <input type="number" step="0.001" className="input" {...form.register("raw_value", { required: true })} />
              </div>
              <div className="md:col-span-2">
                <label className="label">Notes</label>
                <textarea className="input" rows={2} {...form.register("notes")} />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <button type="submit" className="btn-primary">
                  <Save size={16} /> Save reading
                </button>
              </div>
            </form>
          )}
          {tab === "csv" && (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-4">
                <p className="text-sm text-slate-600">
                  CSV must include columns: <code className="px-1 bg-slate-100 rounded">meter_number, reading_date, raw_value, unit_of_measure, notes</code>.
                  Dates in <code className="px-1 bg-slate-100 rounded">YYYY-MM-DD</code> format.
                </p>
                <a
                  href={`${import.meta.env.VITE_API_BASE_URL}/api/v1/readings/csv-template`}
                  className="btn-secondary whitespace-nowrap"
                  download
                >
                  <Download size={14} /> Download template
                </a>
              </div>
              <label className="block border-2 border-dashed border-slate-300 rounded-xl p-10 text-center cursor-pointer hover:border-brand-500 transition">
                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                />
                <Upload className="mx-auto text-slate-400 mb-2" size={32} />
                <p className="text-sm text-slate-600">
                  {uploading ? "Uploading…" : "Click or drag a CSV file here"}
                </p>
              </label>
              {csvResult && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="card p-4 text-center">
                    <p className="text-xs text-slate-500">Imported</p>
                    <p className="text-2xl font-semibold text-brand-700">{csvResult.imported}</p>
                  </div>
                  <div className="card p-4 text-center">
                    <p className="text-xs text-slate-500">Skipped</p>
                    <p className="text-2xl font-semibold text-amber-700">{csvResult.skipped}</p>
                  </div>
                  <div className="card p-4 text-center">
                    <p className="text-xs text-slate-500">Errors</p>
                    <p className="text-2xl font-semibold text-red-700">{csvResult.errors.length}</p>
                  </div>
                </div>
              )}
              {csvResult && csvResult.errors.length > 0 && (
                <DataTable
                  columns={[
                    { key: "row", header: "Row", render: (e) => e.row },
                    { key: "reason", header: "Reason", render: (e) => e.reason },
                  ]}
                  rows={csvResult.errors.map((e, i) => ({ ...e, id: String(i) }))}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
